// useAuth.tsx
import React, {
  useContext,
  createContext,
  FC,
  ReactNode,
  useState,
  useEffect,
  useMemo,
  useCallback
} from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { client } from 'graphql/apolloClient';
import { SIGN_IN, GET_USER_INFO_BY_USER_ID } from 'graphql/service';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { selectedRooms, userInfoState, paymentInfoState, couponState,couponSuccessState } from 'states/CheckOutStates/atoms'
import jwt_decode from "jwt-decode"
import { JsonWebToken } from 'types/commonTypes'
import { globalErrorState} from 'states/CommonStates/atoms';

/**
 *  A custom hook for auth
 *  useAuth - useContext(AuthContext)
 *  AuthProvider - as top level container to wrap around the child component
 */
interface AuthContextType {
  validToken: string | null;
  userId: string | null;
  userSignIn: (email: string, password: string, redirectPath: string) => void;
  loginWithToken: (token: string, redirectPath?: string) => void;
  userLogout: () => void;
  // added to refresh user info for personal details pages
  refreshUserInfo: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export const AuthProvider: FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {


  const history = useHistory();
  const setUserInfo = useSetRecoilState(userInfoState)
  const clearSelectedRooms = useResetRecoilState(selectedRooms)
  const clearUserInfo = useResetRecoilState(userInfoState)
  const clearPaymentInfo = useResetRecoilState(paymentInfoState)
  const clearCoupon = useResetRecoilState(couponState)
  const clearCouponSuccess = useResetRecoilState(couponSuccessState)
  
  const [ validToken, setValidToken ] = useState(localStorage.getItem('token'))
  const [ userId, setUserId ] = useState(localStorage.getItem('userId'))
  const [pendingRedirectPath, setPendingRedirectPath] = useState<string | null>(null);


  const setErrorState = useSetRecoilState(globalErrorState);


  const [getUserInfoByUserId] = useLazyQuery(GET_USER_INFO_BY_USER_ID,
    {
      onCompleted: (userInfoData)=> {
        if (userInfoData.userById) {
          setUserInfo((userInfo) => ({
            ...userInfo,
            userId: userInfoData.userById.userId,
            fullNameInput: userInfoData.userById.fullName || '',
            emailInput: userInfoData.userById.email || '',
            mailAddressInput: userInfoData.userById.address || '',
            addressLine2Input: userInfoData.userById.addressLine2 || '',
            cityInput: userInfoData.userById.city || '',
            provinceInput: userInfoData.userById.province || '',
            postalCodeInput: userInfoData.userById.postalCode || '',
            countryInput: userInfoData.userById.country || '',
            phoneNumberInput: userInfoData.userById.phoneNumber || '',
            dateOfBirth: userInfoData.userById.dateOfBirth || ''
          }));
        } else {
          localStorage.clear()
          setUserId(null)
          setValidToken(null)
          history.push('/sign-in')
        }
      },
      onError: (error) => {
        const content = error.message
        console.log("Error: ", content);
        if (error.message === "Token has expired. Please login again.") {
          setErrorState((prevState)=>({
            ...prevState,
            isVisible: true,
            text: "Your session has expired. Please log in again."
          }));
          localStorage.clear()
          setUserId(null)
          setValidToken(null)
        }
        //console.log(content)
      }
    }
  )

  const [signIn]  = useMutation(
    SIGN_IN,
    { 
      onCompleted: (signInData) => {
        const token = signInData.authenticate?.accessToken        
        const decoded: JsonWebToken = jwt_decode(token)
        if(decoded.user_type === 'STAFF') {
          const content = 'Unauthorized User Type!'
          console.log(content)
          userLogout()
        } else {
          localStorage.setItem('userId', decoded.sub)
          localStorage.setItem('token', token)
          setUserId(decoded.sub)
          setValidToken(token)
          history.push(pendingRedirectPath || '/account/dashboard');
        }
      },
      onError(error) {
        const content = error.message
        
        setErrorState((prevState)=>({
          ...prevState,
          isVisible: true,
          text: error.message
        }));
        
        console.log(content)
      }
    }
  )



  useEffect(()=>{
    if(userId) {
      getUserInfoByUserId({ variables: { userId: userId } })
    }
  },[userId, getUserInfoByUserId])

  const userLogout = useCallback(() => {

    localStorage.clear()
    client.clearStore()
    setValidToken(null)
    clearSelectedRooms()
    clearUserInfo()
    clearPaymentInfo()
    clearCoupon()
    clearCouponSuccess()
    console.log('Log out successfully!')
    history.push('/')
  }, [history, clearSelectedRooms, clearUserInfo, clearPaymentInfo, clearCoupon, clearCouponSuccess])

  /**
   * user signIn function to set userId, tenantId, and access token
   * to state and localStorage
   * @param {string} account - user email or phone number
   * @param {string} password - user password
   * @param {string} redirectPath  - after user successfully signIn, can redirect different path depending on where to use
   */
  
  const userSignIn = useCallback((account: string, password: string, redirectPath: string) => {
    
    setPendingRedirectPath(redirectPath);

    signIn({
      variables: {
        input: {
          login: account,
          password: password
        }
      }
    })  
  }, [signIn])



  const loginWithToken = useCallback((token: string, redirectPath: string = '/account/dashboard') => {
    const decoded: JsonWebToken = jwt_decode(token)
  
    if (decoded.user_type === 'STAFF') {
      userLogout()
      return
    }
  
    localStorage.setItem('userId', decoded.sub)
    localStorage.setItem('token', token)
  
    setUserId(decoded.sub)
    setValidToken(token)
  
    history.push(redirectPath)
  }, [history, userLogout])


  // added to refresh user info for personal details pages
  const refreshUserInfo = useCallback(() => {
    if (userId) {
      getUserInfoByUserId({ variables: { userId }, fetchPolicy: 'no-cache', });
    }
  }, [userId, getUserInfoByUserId]);


  const memoedValue = useMemo(
    () => ({
      validToken,
      userId,
      userSignIn,
      userLogout,
      loginWithToken,
      // added to refresh user info for personal details pages
      refreshUserInfo,
    }),
    [validToken, userId, userLogout, userSignIn, refreshUserInfo, loginWithToken]
  )


  return (
    <AuthContext.Provider value={memoedValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
}

